const buildTranspileConfig = () => ({
  presets: [
    ["react", { runtime: "automatic", development: false }],
    ["env", { modules: "commonjs" }],
  ],
  filename: "LiveMockupScreen.jsx",
});

const SIBLING_SCREEN_IMPORT_PATTERN = /^\.\/[A-Za-z][A-Za-z0-9]*$/;

const extractRelativeImportSpecifiers = (source) => {
  const importPattern =
    /import\s+(?:\{[^}]*\}|\*\s+as\s+\w+|\w+)(?:\s*,\s*(?:\{[^}]*\}|\w+))?\s*from\s*["'](\.[^"']*)["']/g;
  return [...source.matchAll(importPattern)].map((match) => match[1]);
};

// A screen's source often imports sibling files from the SAME hill (e.g.
// "./ReductionsChrome" for shared nav/tabs, "./ReductionsShared" for shared
// row components) — these are per-hill, not curated npm packages, so they
// can't live in liveCompileRegistry.js's static registry. Since every real
// hill's screen files are already part of this same app bundle, a webpack
// dynamic import resolves the real, currently-deployed sibling module
// directly — no extra network fetch, no re-transpiling its source.
const loadSiblingScreenModule = ({ hillKey, specifier }) => {
  const moduleName = specifier.replace(/^\.\//, "");
  return import(`../${hillKey}/screens/${moduleName}.js`);
};

const buildSiblingScreenRegistry = async ({ hillKey, source }) => {
  if (!hillKey) return {};
  const specifiers = extractRelativeImportSpecifiers(source).filter((specifier) =>
    SIBLING_SCREEN_IMPORT_PATTERN.test(specifier),
  );
  const entries = await Promise.all(
    specifiers.map(async (specifier) => [specifier, await loadSiblingScreenModule({ hillKey, specifier })]),
  );
  return Object.fromEntries(entries);
};

// Compiles agent-authored source directly in the browser — no build, no
// deploy, sub-second. Only ever called with source the mockup-editor agent
// generated (never raw reviewer input), same trust boundary as any AI
// live-preview tool. new Function is the actual mechanism, not a shortcut
// around one.
const executeModule = (code, resolveLiveImport) => {
  const moduleShape = { exports: {} };
  // eslint-disable-next-line no-new-func
  const factory = new Function("module", "exports", "require", code);
  factory(moduleShape, moduleShape.exports, resolveLiveImport);
  return moduleShape.exports;
};

// @babel/standalone (the whole Babel compiler) and liveCompileRegistry (every
// curated icon + package export) are both dynamically imported so they land
// in a separate chunk, fetched only the first time a reviewer's browser
// actually needs to compile a live change — not on every mockup page load.
// The module system dedupes repeat import() calls for the same specifier, so
// no manual caching is needed here.
const compileLiveComponent = async (source, { hillKey } = {}) => {
  const [Babel, { resolveLiveImport }, siblingScreenRegistry] = await Promise.all([
    import("@babel/standalone"),
    import("./liveCompileRegistry"),
    buildSiblingScreenRegistry({ hillKey, source }),
  ]);
  const { code } = Babel.transform(source, buildTranspileConfig());
  const moduleExports = executeModule(code, (specifier) => resolveLiveImport(specifier, siblingScreenRegistry));
  const Component = moduleExports.default || moduleExports;
  if (typeof Component !== "function") {
    throw new Error("Live-compiled source did not produce a renderable component.");
  }
  return Component;
};

export { compileLiveComponent };
